import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { ErrorDisplay } from '@/components/common/error-display'
import { TrajectoryPathVisualization } from '@/features/trajectory-paths'
import {
  ConvexDirectReportPositionProgressChart,
  ConvexDirectReportPositionRadarChart,
} from '@/features/users'
import { useDirectReportProfile } from '@/features/users/services/users.convex'
import { useCurrentOrganizationId, usePermissions } from '@/features/auth'

export const Route = createFileRoute(
  '/(dashboard)/direct-reports/$directReportId/career-path/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      name: (search.name as string) || undefined,
    }
  },
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Direct Reports', href: '/direct-reports' },
      { title: search.name || params.directReportId },
      { title: 'Career Path' },
    ],
  }),
})

function RouteComponent() {
  const { directReportId } = Route.useParams()
  const { data, isLoading, error } = useDirectReportProfile(directReportId)
  const currentOrgId = useCurrentOrganizationId()
  const { hasOrganizationRole } = usePermissions()
  const isPrivilegedView = currentOrgId
    ? hasOrganizationRole(currentOrgId, 'admin') ||
      hasOrganizationRole(currentOrgId, 'manager')
    : false

  const trajectorySteps = useMemo(
    () => data?.trajectory?.steps ?? [],
    [data?.trajectory?.steps],
  )

  if (isLoading) {
    return <div>Loading career path...</div>
  }

  if (error) {
    return <ErrorDisplay error={error} title="Error loading career path" />
  }

  return (
    <div className="space-y-6 mt-2">
      {/* Trajectory Path Visualization */}
      {trajectorySteps.length > 0 ? (
        <TrajectoryPathVisualization
          trajectoryPath={trajectorySteps}
          pathName={data?.trajectory?.name ?? ''}
          currentLevel={data?.position?.level}
        />
      ) : (
        <div className="p-6 bg-muted/30 rounded-lg text-center text-muted-foreground">
          No trajectory path found for this user.
        </div>
      )}

      {/* Assessment Charts Grid — privileged users only */}
      {isPrivilegedView && (
        <div className="grid grid-cols-3 gap-6">
          {/* Position Progress Chart - 2/3 width */}
          <div className="col-span-2">
            <ConvexDirectReportPositionProgressChart userId={directReportId} />
          </div>

          {/* Radar Chart - 1/3 width */}
          <div className="col-span-1">
            <ConvexDirectReportPositionRadarChart userId={directReportId} />
          </div>
        </div>
      )}
    </div>
  )
}
